import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "V2.0/common/table";
import { Heading } from "V2.0/common/heading";
import { Button } from "V2.0/common/button";
import * as campaignService from "V2.0/services/campaignService";
import { Badge, BadgeButton } from "V2.0/common/badge";
import Pagination from "V2.0/components/Pagination";
import { message, Tooltip } from "antd";
import {
  ClockIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  PlusIcon,
  XMarkIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import {
  Dropdown,
  DropdownButton,
  DropdownDescription,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSection,
} from "V2.0/common/dropdown";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import { BulkContact } from "./BulkUpload";
import { TrashIcon } from "@heroicons/react/24/solid";
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogBody,
} from "V2.0/common/dialog";
import { DialogTitleWithClose } from "V2.0/HomePages/HomeModule/CustomAPIs/components/Dialog/DialogTitleWithClose";
import { useOutletContext, useNavigate } from "react-router-dom";
import { SingleContact } from "./SingleContact";
import { Select } from "V2.0/common/select";
import { StartAndEndDateField } from "V2.0/HomePages/Downloads/components/StartAndEndDateField";
import InputComponent from "V2.0/components/InputComponent";
import { useAllAssistantOptions } from "./useAllAssistantOptions";
import { useAssistants } from "../Assistants";
import { ConversationSourceIcon } from "V2.0/HomePages/Threads/components/conversation_item";
import dayjs from "dayjs";
import { useDebouncedSearch } from "V2.0/HomePages/Threads/models/useDebouncedSearch";
import { showErrorToast } from "V2.0/utils";
import BulkDelete from "./BulkDelete";

const formatFollowUpTime = (timestamp) => {
  const date = new Date(Number(timestamp));
  return dayjs(date).format("MMM DD, YYYY, HH:mm");
};

const CAMPAIGN_STATUS = {
  SCHEDULED: "Scheduled",
  COMPLETED: "Completed",
  ONGOING: "Ongoing",
  FAILED: "Failed",
  UNANSWERED: "Unanswered",
  VOICEMAIL: "Voicemail",
  BLOCKED: "Blocked",
  DELIVERED: "Delivered",
};

const CAMPAIGN_STATUS_LABEL_COLORS = {
  [CAMPAIGN_STATUS.COMPLETED]: "green",
  [CAMPAIGN_STATUS.DELIVERED]: "green",
  [CAMPAIGN_STATUS.VOICEMAIL]: "violet",
  [CAMPAIGN_STATUS.ONGOING]: "yellow",
  [CAMPAIGN_STATUS.SCHEDULED]: "amber",
  [CAMPAIGN_STATUS.UNANSWERED]: "pink",
  [CAMPAIGN_STATUS.FAILED]: "red",
  [CAMPAIGN_STATUS.BLOCKED]: "red",
};

const campaignStatusOptions = Object.values(CAMPAIGN_STATUS).map((status) => {
  return { id: status, label: status, value: status };
});

const campaignStatusOptionsWithDefault = [
  { id: "none", label: "(None)", value: "" },
  ...campaignStatusOptions,
];

function stripNonDigits(input) {
  return input.replace(/\D/g, ""); // \D matches any non-digit character
}

function useCampaigns(projectId, assistants) {
  const [campaigns, setCampaigns] = useState({
    totalPages: 0,
    totalCampaigns: 0,
    campaigns: [],
    currentPage: 1,
    pageSize: 50,
    isLoading: true,
    isReset: false,
    searchTerm: "",
  });

  const [filterParams, setFilterParams] = useState({
    callStatus: "",
    startDate: "",
    endDate: "",
    assistantId: "",
  });

  const isLoading = campaigns.isLoading;
  const setIsLoading = (state) =>
    setCampaigns((prevState) => {
      return {
        ...prevState,
        isLoading: state,
      };
    });

  const transformCampaigns = (campaigns) => {
    if (assistants.length > 0) {
      const assistantsMap = assistants.reduce((acc, assistant) => {
        return {
          ...acc,
          [assistant._id]: assistant,
        };
      }, {});

      const updatedCampaigns = campaigns.map((campaign) => {
        const { assistant } = campaign;
        return {
          ...campaign,
          assistant: assistantsMap[assistant],
        };
      });

      return updatedCampaigns;
    }

    return campaigns;
  };

  const updateDate = (date) => {
    const { startDate, endDate } = date;

    setFilterParams((prevState) => {
      return {
        ...prevState,
        startDate,
        endDate,
      };
    });
  };

  const updateCallStatus = (callStatus) => {
    setFilterParams((prevState) => {
      return {
        ...prevState,
        callStatus,
      };
    });
  };

  const updateAssistantId = (assistantId) => {
    setFilterParams((prevState) => {
      return {
        ...prevState,
        assistantId,
      };
    });
  };

  const fetchCampaigns = useCallback(
    async function fetchCampaigns() {
      try {
        setIsLoading(true);

        const response = await campaignService.getAllCalls(projectId);
        const { data } = response;

        setCampaigns(data.reverse());
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error(error);
      }
    },
    [projectId]
  );

  const fetchPaginatedCampaigns = useCallback(
    async (projectId, page, pageSize) => {
      setIsLoading(true);
      try {
        const response = await campaignService.getAllCampaignsPaginated(
          projectId,
          { page, pageSize }
        );
        if (response.status === 200) {
          const { data } = response;
          const campaingsWithAssistants = transformCampaigns(data.campaigns);
          setCampaigns({
            ...data,
            campaigns: campaingsWithAssistants,
            isLoading: false,
          });
        }
      } catch (e) {
        showErrorToast(e, "Failed to get campaigns");
        setIsLoading(false);
      }
    },
    [assistants]
  );

  const fetchFilteredCampaigns = useCallback(
    async (projectId, page, pageSize, searchTerm) => {
      setIsLoading(true);
      const { startDate, endDate, assistantId, callStatus } = filterParams;

      const params = {
        page,
        pageSize,
      };

      const payload = {
        ...(Boolean(searchTerm) ? { searchTerm } : {}),
        ...(Boolean(startDate) ? { startDate } : {}),
        ...(Boolean(endDate) ? { endDate } : {}),
        ...(Boolean(assistantId) ? { assistantId } : {}),
        ...(Boolean(callStatus) ? { callStatus } : {}),
      };

      try {
        const response = await campaignService.getFilteredCampaignsPaginated(
          projectId,
          params,
          payload
        );
        const { data } = response;
        const campaingsWithAssistants = transformCampaigns(data.campaigns);
        setCampaigns({
          ...data,
          campaigns: campaingsWithAssistants,
          isLoading: false,
        });
      } catch (e) {
        showErrorToast(e, "Failed to get campaigns");
        setIsLoading(false);
      }
    },
    [assistants, filterParams]
  );

  const refetchCampaings = (searchTerm) => {
    const { assistantId, startDate, endDate, callStatus } = filterParams;
    const isFiltersApplied =
      Boolean(assistantId) ||
      Boolean(startDate) ||
      Boolean(endDate) ||
      Boolean(callStatus);

    if (isFiltersApplied) {
      fetchFilteredCampaigns(projectId, 1, 50, searchTerm);
    } else {
      fetchPaginatedCampaigns(projectId, 1, 50);
    }
  };

  const resetFilters = () => {
    setFilterParams({
      startDate: "",
      endDate: "",
      callStatus: "",
      assistantId: "",
    });
    fetchPaginatedCampaigns(projectId, 1, 50);
  };

  const resetAssistant = (searchTerm) => {
    setFilterParams((prevState) => {
      return {
        ...prevState,
        assistantId: "",
      };
    });
    setCampaigns((prevState) => {
      return {
        ...prevState,
        isReset: true,
        searchTerm,
      };
    });
  };

  const resetCallStatus = (searchTerm) => {
    setFilterParams((prevState) => {
      return {
        ...prevState,
        callStatus: "",
      };
    });
    setCampaigns((prevState) => {
      return {
        ...prevState,
        isReset: true,
        searchTerm,
      };
    });
  };

  const resetDate = (searchTerm) => {
    setFilterParams((prevState) => {
      return {
        ...prevState,
        startDate: "",
        endDate: "",
      };
    });
    setCampaigns((prevState) => {
      return {
        ...prevState,
        isReset: true,
        searchTerm,
      };
    });
  };

  const addUserToCampaign = useCallback(
    async (data, onSuccess, onError) => {
      try {
        const response = await campaignService.addToCampaign(projectId, data);
        onSuccess(response);
        refetchCampaings("");
      } catch (error) {
        return onError(error);
      }
    },
    [projectId]
  );

  const deleteUserFromCampaign = useCallback(
    async (campaignId, onSuccess, onError) => {
      try {
        const response = await campaignService.deleteFromCampaign({
          projectId,
          campaignId,
        });

        onSuccess(response);
        refetchCampaings("");
      } catch (error) {
        onError(error);
      }
    },
    [projectId]
  );

  useEffect(() => {
    if (campaigns.isReset) {
      refetchCampaings(campaigns.searchTerm);
    }
  }, [campaigns.isReset]);

  return {
    campaignData: campaigns,
    isCampaignsLoading: isLoading,
    fetchCampaigns,
    fetchPaginatedCampaigns,
    fetchFilteredCampaigns,
    addUserToCampaign,
    deleteUserFromCampaign,
    assistantId: filterParams.assistantId,
    callStatus: filterParams.callStatus,
    startDate: filterParams.startDate,
    endDate: filterParams.endDate,
    updateAssistantId,
    updateCallStatus,
    updateDate,
    resetFilters,
    resetAssistant,
    resetCallStatus,
    resetDate,
  };
}

function DeleteConfirmation({ isOpen, onClose, onConfirm, item }) {
  return (
    isOpen && (
      <Dialog open={isOpen} onClose={onClose}>
        <DialogTitle className="font-medium">
          <DialogTitleWithClose
            title={`Delete ${item.userNumber} from campaign?`}
            onClose={onClose}
          />
        </DialogTitle>
        <DialogActions>
          <Button outline onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onConfirm}>
            <TrashIcon /> Delete
          </Button>
        </DialogActions>
      </Dialog>
    )
  );
}

function Filters(props) {
  const {
    onClose,
    onFiltersReset,
    onFiltersApply,
    updateCallStatus,
    callStatus,
    startDate,
    endDate,
    updateDate,
    assistantOptions,
    filterAssistantId,
    updateAssistantId,
    disableApply,
  } = props;

  return (
    <Dialog size="sm" open={true} onClose={onClose}>
      <DialogTitle>Filters</DialogTitle>
      <DialogBody>
        <div className="flex flex-col items-start gap-y-8">
          <div className="flex w-full flex-col items-start gap-y-2">
            <p className="text-sm font-semibold text-gray-500">Status:</p>
            <Select
              onChange={(e) => updateCallStatus(e.target.value)}
              value={callStatus}
            >
              {campaignStatusOptionsWithDefault.map((option) => (
                <option key={option.id} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col w-full items-start gap-y-2">
            <p className="text-sm font-semibold text-gray-500">Date Range:</p>
            <StartAndEndDateField
              hideLabel={true}
              error=""
              date={[startDate, endDate]}
              setError={() => {}}
              setDate={updateDate}
            />
          </div>
          <div className="flex flex-col w-full  items-start gap-y-2">
            <p className="text-sm font-semibold text-gray-500">Assistant:</p>
            <div className="w-full">
              <InputComponent
                type="select"
                options={assistantOptions}
                value={filterAssistantId}
                onChange={(e) => updateAssistantId(e.target.value)}
              />
            </div>
          </div>
        </div>
      </DialogBody>
      <DialogActions>
        {!disableApply ? (
          <Button outline onClick={onFiltersReset}>
            Reset Filters
          </Button>
        ) : (
          <Button outline onClick={onClose}>
            Cancel
          </Button>
        )}
        <Button disabled={disableApply} onClick={onFiltersApply}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function FilterApplied(props) {
  const { children, onClose } = props;
  return (
    <BadgeButton className="border rounded-lg" color="white" onClick={onClose}>
      <div className="flex items-center justify-between gap-x-4">
        {children} <XMarkIcon className="size-4" />
      </div>
    </BadgeButton>
  );
}

function HomeCampaigns() {
  const { projects, activeProject, projectId } = useOutletContext();
  const navigate = useNavigate();

  const [addContactDialog, setAddContactDialog] = useState({
    isOpen: false,
    type: undefined,
  });

  let [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);

  const [showDelete, setShowDelete] = useState(null);
  const [openFilters, setOpenFilters] = useState(false);

  const searchElementRef = useRef(null);

  const { assistants, fetchAssistants, isLoading } = useAssistants(projectId);
  const { assistantOptions, assistantId, setAssistantId } =
    useAllAssistantOptions(assistants);
  const initialLoadRef = useRef(true);
  const { searchTerm, updateSearchTerm } = useDebouncedSearch();

  const {
    campaignData,
    isCampaignsLoading: pageLoading,
    fetchPaginatedCampaigns,
    addUserToCampaign,
    deleteUserFromCampaign,
    assistantId: filterAssistantId,
    callStatus,
    startDate,
    endDate,
    resetFilters,
    updateAssistantId,
    updateDate,
    updateCallStatus,
    fetchFilteredCampaigns,
    resetAssistant,
    resetDate,
    resetCallStatus,
  } = useCampaigns(projects[activeProject]?._id, assistants);

  const itemsPerPage = campaignData.pageSize; // Define how many items per page
  const currentPage = campaignData.currentPage;
  const filteredCampaigns = campaignData.campaigns;

  // const { handlePageChange, paginatedItems: filteredAndPaginatedCampaigns } =
  //   usePagination(filteredCampaigns, itemsPerPage);

  useEffect(() => {
    if (projectId) {
      fetchAssistants();
    }
  }, [projectId]);

  useEffect(() => {
    if (!isLoading) {
      fetchPaginatedCampaigns(projectId, 1, 50);
    }
  }, [isLoading]);

  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }

    if (!initialLoadRef.current && Boolean(searchTerm)) {
      fetchFilteredCampaigns(projectId, 1, 50, searchTerm);
    } else {
      fetchPaginatedCampaigns(projectId, 1, 50);
    }
  }, [searchTerm]);

  const handleApplyFilters = () => {
    fetchFilteredCampaigns(projectId, 1, 50, searchTerm);
  };

  const isFiltersApplied =
    Boolean(filterAssistantId) ||
    Boolean(startDate) ||
    Boolean(endDate) ||
    Boolean(callStatus);

  const assistant = useMemo(() => {
    if (filterAssistantId) {
      const _assistant = assistants.find(
        (item) => item._id === filterAssistantId
      );
      return _assistant;
    }

    return {};
  }, [filterAssistantId, assistants]);

  return (
    <>
      <div className="mx-auto p-4 px-16">
        <div className="flex w-full flex-wrap items-end justify-between gap-4 mb-8 dark:border-white/10">
          <Heading>Campaigns</Heading>

          <div className="flex gap-4">
            <div className="flex gap-x-2">
              <Button
                outline
                onClick={() => navigate("/campaign/configuration")}
                type="button"
                className="flex items-center space-x-2"
              >
                <Cog6ToothIcon className="h-5 w-5" />
                <span>Configuration</span>
              </Button>
              <Dropdown>
                <DropdownButton>
                  <PlusIcon /> Add
                  <ChevronDownIcon />
                </DropdownButton>
                <DropdownMenu className="min-w-48">
                  <DropdownSection>
                    <DropdownItem
                      onClick={() => setAddContactDialog({ isOpen: true })}
                    >
                      <DropdownLabel>Single Contact</DropdownLabel>
                    </DropdownItem>
                  </DropdownSection>
                  <DropdownDivider />
                  <DropdownSection>
                    <DropdownItem onClick={() => setIsBulkUploadOpen(true)}>
                      <DropdownLabel>Bulk upload</DropdownLabel>
                      <DropdownDescription>
                        Import Contacts in Bulk
                      </DropdownDescription>
                    </DropdownItem>
                  </DropdownSection>
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-y-8">
          <div className="flex justify-between gap-x-2">
            {/* {searchTerm && (
                <FilterApplied
                  onClose={() => {
                    resetSearchTerm();
                  }}
                >
                  {searchTerm}
                </FilterApplied>
              )} */}
            <div className="flex gap-x-4">
              <div>
                <Button onClick={() => setOpenFilters(true)}>
                  <FunnelIcon /> Filters
                </Button>
              </div>
              <div className="flex flex-col gap-y-2">
                <div className="flex gap-x-2">
                  {callStatus && (
                    <FilterApplied
                      onClose={() => {
                        resetCallStatus(searchTerm);
                      }}
                    >
                      <Badge color={CAMPAIGN_STATUS_LABEL_COLORS[callStatus]}>
                        {callStatus}
                      </Badge>
                    </FilterApplied>
                  )}
                  {startDate && endDate && (
                    <FilterApplied
                      onClose={() => {
                        resetDate(searchTerm);
                      }}
                    >
                      {formatFollowUpTime(startDate)} -{" "}
                      {formatFollowUpTime(endDate)}
                    </FilterApplied>
                  )}
                  {filterAssistantId && (
                    <FilterApplied
                      onClose={() => {
                        resetAssistant(searchTerm);
                      }}
                    >
                      {assistant?.name}
                    </FilterApplied>
                  )}
                  {isFiltersApplied && (
                    <Button outline onClick={resetFilters}>
                      Clear All
                    </Button>
                  )}
                  {isFiltersApplied &&
                    !openFilters &&
                    !campaignData.isLoading &&
                    Boolean(campaignData.totalCampaigns) && (
                      <div className="flex">
                        <BulkDelete
                          totalItems={campaignData.totalCampaigns}
                          appliedFilters={{
                            callStatus,
                            startDate,
                            endDate,
                            filterAssistantId,
                          }}
                          refetchFilteredCampaigns={handleApplyFilters}
                          workspaceId={projectId}
                        />
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div className="w-80">
              <input
                ref={searchElementRef}
                type="search"
                onChange={(e) => {
                  const sanitizedSearchTerm = stripNonDigits(e.target.value);
                  updateSearchTerm(sanitizedSearchTerm);
                }}
                placeholder="Search by Phone Number"
                className="w-full rounded border border-gray-300 px-4 py-2 text-sm text-gray-600 placeholder-gray-400 focus:outline-none focus:border-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="">
            {filteredCampaigns &&
              filteredCampaigns.length === 0 &&
              !pageLoading &&
              !isFiltersApplied && (
                <button
                  onClick={() => setAddContactDialog({ isOpen: true })}
                  type="button"
                  className="relative block h-[80dvh] w-full rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none"
                >
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 14v20c0 4.418 7.163 8 16 8 1.381 0 2.721-.087 4-.252M8 14c0 4.418 7.163 8 16 8s16-3.582 16-8M8 14c0-4.418 7.163-8 16-8s16 3.582 16 8m0 0v14m0-4c0 4.418-7.163 8-16 8S8 28.418 8 24m32 10v6m0 0v6m0-6h6m-6 0h-6"
                    />
                  </svg>
                  <span className="mt-2 block text-sm font-semibold text-gray-900">
                    Your campaigns table is empty
                  </span>
                  <p className="mt-5">
                    The campaign is the best way to send an outbound call or
                    SMS. <br />
                    It always comes with many features, such as the ability to
                    schedule your campaign, <br /> follow up, configure timings
                    for follow-ups, and more.
                  </p>
                </button>
              )}
            {filteredCampaigns &&
              filteredCampaigns.length === 0 &&
              !pageLoading &&
              isFiltersApplied && (
                <div className="flex items-center mx-auto h-80 border rounded-lg">
                  <div className="flex flex-col mx-auto gap-y-4">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 14v20c0 4.418 7.163 8 16 8 1.381 0 2.721-.087 4-.252M8 14c0 4.418 7.163 8 16 8s16-3.582 16-8M8 14c0-4.418 7.163-8 16-8s16 3.582 16 8m0 0v14m0-4c0 4.418-7.163 8-16 8S8 28.418 8 24m32 10v6m0 0v6m0-6h6m-6 0h-6"
                      />
                    </svg>
                    <p className="text-sm font-semibold">No Campaings found</p>
                  </div>
                </div>
              )}

            {pageLoading && (
              <div className="h-[80dvh]">
                <div className="h-[74dvh] overflow-scroll">
                  <Table className="animate-pulse">
                    <TableHead>
                      <TableRow>
                        <TableHeader>Campaign</TableHeader>
                        <TableHeader>Assistant</TableHeader>
                        <TableHeader>Follow Up</TableHeader>
                        <TableHeader>Created At</TableHeader>
                        <TableHeader>Action</TableHeader>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {Array.from({ length: 20 }).map((_, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <div className="h-6 bg-gray-100 rounded"></div>
                          </TableCell>
                          <TableCell>
                            <div className="h-6 bg-gray-100 rounded"></div>
                          </TableCell>
                          <TableCell>
                            <div className="h-6 bg-gray-100 rounded"></div>
                          </TableCell>
                          <TableCell>
                            <div className="h-6 bg-gray-100 rounded"></div>
                          </TableCell>
                          <TableCell>
                            <div className="h-6 bg-gray-100 rounded"></div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {filteredCampaigns &&
              filteredCampaigns.length > 0 &&
              !pageLoading && (
                <div className="h-[80dvh]">
                  <div className="h-[74dvh] overflow-scroll">
                    <Table className="">
                      <TableHead>
                        <TableRow>
                          {/* <TableHeader>UserIdentifier</TableHeader>
                          <TableHeader>Type</TableHeader>
                          <TableHeader>Next Follow-Up Time</TableHeader>
                          <TableHeader>Action</TableHeader> */}
                          <TableHeader>Campaign</TableHeader>
                          <TableHeader>Assistant</TableHeader>
                          <TableHeader>Last Call Status</TableHeader>
                          <TableHeader>Follow Up</TableHeader>
                          <TableHeader>Created At</TableHeader>
                          <TableHeader>Action</TableHeader>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {filteredCampaigns.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="">
                              <div className="">
                                <div className="flex items-center gap-x-2">
                                  {
                                    ConversationSourceIcon[
                                      item.sourceOfConversation
                                    ]
                                  }
                                  <p className="text-sm font-semibold">
                                    {item.userIdentifier}
                                  </p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="truncate max-w-[150px]">
                              <Tooltip title={item?.assistant?.name}>
                                {item?.assistant?.name}
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              {" "}
                              <Badge
                                color={
                                  CAMPAIGN_STATUS_LABEL_COLORS[item.callStatus]
                                }
                              >
                                {item.callStatus}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {item.userResponded === true ? (
                                  <Badge color="emerald">User Responded</Badge>
                                ) : item.nextFollowUpTime === 0 ? (
                                  <Badge>No follow-up scheduled</Badge>
                                ) : (
                                  <Badge color="amber">
                                    <ClockIcon className="size-4" />
                                    {formatFollowUpTime(item.nextFollowUpTime)}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {formatFollowUpTime(item.createdAt)}
                            </TableCell>
                            <TableCell>
                              <Button
                                outline
                                onClick={() =>
                                  setShowDelete({
                                    campaignId: item._id,
                                    userNumber: item.userIdentifier,
                                  })
                                }
                              >
                                <TrashIcon />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="relative bottom-0">
                    <Pagination
                      currentPage={currentPage}
                      totalItems={campaignData.totalCampaigns}
                      itemsPerPage={campaignData.pageSize}
                      onPageChange={(page) => {
                        if (isFiltersApplied) {
                          fetchFilteredCampaigns(
                            projectId,
                            page,
                            campaignData.pageSize
                          );
                        } else {
                          fetchPaginatedCampaigns(
                            projectId,
                            page,
                            campaignData.pageSize
                          );
                        }
                      }}
                    />
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
      {addContactDialog.isOpen && (
        <SingleContact
          addUserToCampaign={addUserToCampaign}
          dialogState={addContactDialog}
          onClose={() =>
            setAddContactDialog({
              isOpen: false,
              type: undefined,
            })
          }
          projectId={projectId}
        />
      )}
      {openFilters && (
        <Filters
          // searchTerm={searchTerm}
          // searchElementRef={searchElementRef}
          // updateSearchTerm={updateSearchTerm}
          startDate={startDate}
          endDate={endDate}
          updateDate={updateDate}
          assistantOptions={assistantOptions}
          filterAssistantId={filterAssistantId}
          updateAssistantId={updateAssistantId}
          callStatus={callStatus}
          updateCallStatus={updateCallStatus}
          disableApply={!isFiltersApplied}
          onClose={() => {
            setOpenFilters(false);
          }}
          onFiltersReset={() => {
            resetFilters();
            setOpenFilters(false);
          }}
          onFiltersApply={() => {
            handleApplyFilters();
            setOpenFilters(false);
          }}
        />
      )}

      {/* <BulkUpload
          projects={projects}
          activeProject={activeProject}
          isBulkUploadOpen={isBulkUploadOpen}
          setIsBulkUploadOpen={setIsBulkUploadOpen}
        /> */}
      <BulkContact
        projectId={projectId}
        isBulkUploadOpen={isBulkUploadOpen}
        setIsBulkUploadOpen={setIsBulkUploadOpen}
      />
      <DeleteConfirmation
        isOpen={!!showDelete}
        onClose={() => setShowDelete(null)}
        onConfirm={() => {
          deleteUserFromCampaign(
            showDelete.campaignId,
            (response) => {
              if (response.status === 200 || response.status === 201) {
                message.success("Campaign deleted");
              }
            },
            (error) => {
              showErrorToast(error, "Failed to delete campaign");
              console.error(error);
            }
          );
          setShowDelete(null);
        }}
        item={showDelete}
      />
    </>
  );
}

export default HomeCampaigns;
